import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import dayjs from "dayjs";

import Alert from "@mui/material/Alert";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentPasteOutlinedIcon from "@mui/icons-material/ContentPasteOutlined";
import EventNoteIcon from "@mui/icons-material/EventNote";
import GroupIcon from "@mui/icons-material/Group";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import NotificationsIcon from "@mui/icons-material/Notifications";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { DatabaseContext, UserContext } from "../../../context";
import firebase from "../../../firebase";
import { jsonFromArray, makeid } from "../../../utility";
import { sendSharedItemEmails } from "../../shareDialogDelivery";
import DialogWithMessage from "../../DialogWithMessage";
import NoPermissionsMessage from "../../NoPermissionsMessage";
import {
    primaryHoverBorder,
    primaryTint,
    primaryTintBorder,
} from "../../../surfaceTints";
import FileNotificationsDialog from "./FileNotificationsDialog";
import FilePermissionsDialog from "./FilePermissionsDialog";
import LinkFileToEventDialog from "./LinkFileToEventDialog";

const settingButtonSx = {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 1.25,
    p: 1.35,
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    backgroundColor: "background.paper",
    textAlign: "left",
    transition:
        "border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease",
    "&:hover": {
        borderColor: primaryHoverBorder,
        backgroundColor: "action.hover",
        boxShadow: (theme) =>
            theme.palette.mode === "dark"
                ? "0 3px 12px rgba(0, 0, 0, 0.34)"
                : "0 3px 12px rgba(20, 35, 25, 0.06)",
    },
};

function normaliseLink(value) {
    const trimmedValue = value.trim();

    if (!trimmedValue || /\s/.test(trimmedValue)) {
        return null;
    }

    const valueWithProtocol = /^[a-z][a-z\d+.-]*:\/\//i.test(trimmedValue)
        ? trimmedValue
        : `https://${trimmedValue}`;

    try {
        const parsedUrl = new URL(valueWithProtocol);

        if (
            !["http:", "https:"].includes(parsedUrl.protocol) ||
            !parsedUrl.hostname
        ) {
            return null;
        }

        return parsedUrl.toString();
    } catch (error) {
        return null;
    }
}

function escapeHtml(value) {
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

function getLinkPreview(value) {
    const normalisedUrl = normaliseLink(value);

    if (!normalisedUrl) {
        return null;
    }

    const parsedUrl = new URL(normalisedUrl);
    const hostname = parsedUrl.hostname.replace(/^www\./, "").toLowerCase();
    const pathParts = parsedUrl.pathname.split("/").filter(Boolean);

    let youTubeId = "";
    if (hostname === "youtu.be") {
        youTubeId = pathParts[0] || "";
    } else if (
        hostname === "youtube.com" ||
        hostname.endsWith(".youtube.com")
    ) {
        const videoPathIndex = ["embed", "shorts", "live"].findIndex(
            (segment) => pathParts.includes(segment),
        );
        const matchedSegment =
            videoPathIndex >= 0
                ? ["embed", "shorts", "live"][videoPathIndex]
                : null;
        const matchedIndex = matchedSegment
            ? pathParts.indexOf(matchedSegment)
            : -1;

        youTubeId =
            parsedUrl.searchParams.get("v") ||
            (matchedIndex >= 0 ? pathParts[matchedIndex + 1] : "") ||
            "";
    }

    if (youTubeId) {
        return {
            kind: "youtube",
            label: "YouTube",
            hostname,
            sourceUrl: normalisedUrl,
            embedUrl: `https://www.youtube-nocookie.com/embed/${encodeURIComponent(
                youTubeId,
            )}`,
        };
    }

    if (hostname === "vimeo.com" || hostname.endsWith(".vimeo.com")) {
        const vimeoId = [...pathParts]
            .reverse()
            .find((part) => /^\d+$/.test(part));

        if (vimeoId) {
            return {
                kind: "vimeo",
                label: "Vimeo",
                hostname,
                sourceUrl: normalisedUrl,
                embedUrl: `https://player.vimeo.com/video/${vimeoId}`,
            };
        }
    }

    if (hostname === "docs.google.com") {
        const documentMatch = parsedUrl.pathname.match(
            /^\/(document|spreadsheets|presentation)\/d\/([^/]+)/,
        );
        const formMatch = parsedUrl.pathname.match(
            /^\/forms\/d\/(e\/)?([^/]+)/,
        );

        if (documentMatch) {
            return {
                kind: "google",
                label:
                    documentMatch[1] === "spreadsheets"
                        ? "Google Sheets"
                        : documentMatch[1] === "presentation"
                          ? "Google Slides"
                          : "Google Docs",
                hostname,
                sourceUrl: normalisedUrl,
                embedUrl: `${parsedUrl.origin}/${documentMatch[1]}/d/${documentMatch[2]}/preview`,
            };
        }

        if (formMatch) {
            return {
                kind: "google",
                label: "Google Forms",
                hostname,
                sourceUrl: normalisedUrl,
                embedUrl: `${parsedUrl.origin}/forms/d/${formMatch[1] || ""}${
                    formMatch[2]
                }/viewform?embedded=true`,
            };
        }
    }

    if (hostname === "drive.google.com") {
        const fileMatch = parsedUrl.pathname.match(/^\/file\/d\/([^/]+)/);

        if (fileMatch) {
            return {
                kind: "google",
                label: "Google Drive",
                hostname,
                sourceUrl: normalisedUrl,
                embedUrl: `${parsedUrl.origin}/file/d/${fileMatch[1]}/preview`,
            };
        }
    }

    return {
        kind: "website",
        label: hostname,
        hostname,
        sourceUrl: normalisedUrl,
        embedUrl: normalisedUrl,
    };
}

function AvailabilityRow({
    icon,
    title,
    description,
    checked,
    onChange,
    name,
    divided = false,
}) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: { xs: 1.5, sm: 2 },
                py: 1.35,
                borderTop: divided ? "1px solid" : "none",
                borderColor: "divider",
            }}
        >
            <Box sx={{ color: "text.secondary", display: "grid" }}>
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            <Switch
                checked={checked}
                onChange={onChange}
                name={name}
                color="primary"
                inputProps={{ "aria-label": title }}
            />
        </Box>
    );
}

function LinkPreview({ link, title }) {
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setIsLoading(true);
    }, [link?.embedUrl]);

    if (!link) {
        return (
            <Box
                sx={{
                    minHeight: { xs: 270, sm: 340 },
                    px: 3,
                    py: 4,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    background: (theme) =>
                        theme.palette.mode === "dark"
                            ? "radial-gradient(circle at 50% 38%, rgba(31, 184, 94, 0.13), transparent 38%), linear-gradient(145deg, #1b231e 0%, #141a16 100%)"
                            : "radial-gradient(circle at 50% 38%, rgba(0, 149, 61, 0.11), transparent 38%), linear-gradient(145deg, #f7faf7 0%, #e8f0e9 100%)",
                }}
            >
                <Box
                    sx={{
                        width: 76,
                        height: 76,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        color: "primary.main",
                        backgroundColor: (theme) =>
                            alpha(theme.palette.background.paper, 0.86),
                        border: "1px solid",
                        borderColor: primaryTintBorder,
                        boxShadow: (theme) =>
                            theme.palette.mode === "dark"
                                ? "0 14px 34px rgba(0, 0, 0, 0.45)"
                                : "0 14px 34px rgba(20, 60, 35, 0.1)",
                    }}
                >
                    <LanguageOutlinedIcon sx={{ fontSize: 38 }} />
                </Box>
                <Typography variant="h6" sx={{ mt: 2 }}>
                    Your preview will appear here
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.75, maxWidth: 390 }}
                >
                    Paste a YouTube video, Google document or website link to
                    check it before adding it.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Box
                sx={{
                    minHeight: { xs: 270, sm: 340 },
                    height: { xs: 270, sm: 340, md: 390 },
                    position: "relative",
                    overflow: "hidden",
                    backgroundColor: "background.media",
                }}
            >
                {isLoading && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 1,
                            display: "grid",
                            placeItems: "center",
                            // A shade off the frame behind it, so the veil still
                            // reads as a cover rather than a blank panel.
                            backgroundColor: "background.subtle",
                        }}
                    >
                        <Box sx={{ textAlign: "center" }}>
                            <CircularProgress size={30} />
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mt: 1 }}
                            >
                                Loading preview
                            </Typography>
                        </Box>
                    </Box>
                )}
                <Box
                    key={link.embedUrl}
                    component="iframe"
                    src={link.embedUrl}
                    title={title || `Preview of ${link.hostname}`}
                    onLoad={() => setIsLoading(false)}
                    allow={
                        link.kind === "youtube" || link.kind === "vimeo"
                            ? "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            : undefined
                    }
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                    sx={{
                        width: "100%",
                        height: "100%",
                        border: 0,
                        backgroundColor: "background.paper",
                    }}
                />
            </Box>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: { xs: 1.5, sm: 2 },
                    py: 1.25,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                }}
            >
                <LanguageOutlinedIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant="subtitle2"
                        noWrap
                        sx={{ fontWeight: 800 }}
                    >
                        {link.label}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ display: "block" }}
                    >
                        {link.hostname}
                    </Typography>
                </Box>
                <Button
                    component="a"
                    href={link.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    endIcon={<OpenInNewIcon />}
                    sx={{ flexShrink: 0 }}
                >
                    Open
                </Button>
            </Box>
            {link.kind === "website" && (
                <Alert
                    severity="info"
                    icon={<LinkOffOutlinedIcon fontSize="inherit" />}
                    sx={{
                        borderRadius: 0,
                        borderTop: "1px solid",
                        borderColor: "divider",
                        py: 0.25,
                    }}
                >
                    Some websites block embedded previews. You can still add and
                    open the link normally.
                </Alert>
            )}
        </Box>
    );
}

export { getLinkPreview, normaliseLink };

export default function NewLinkPage() {
    const history = useHistory();
    const location = useLocation();
    const { users, calendars, folders, groups } = useContext(DatabaseContext);
    const { currentUserData } = useContext(UserContext);

    const [userCanUpload, setUserCanUpload] = useState();
    const [url, setUrl] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [urlWasSubmitted, setUrlWasSubmitted] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [eventDialogOpen, setEventDialogOpen] = useState(false);
    const [events, setEvents] = useState([]);
    const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
    const [notificationsDialogOpen, setNotificationsDialogOpen] =
        useState(false);

    const [allowComments, setAllowComments] = useState(true);
    const [managers, setManagers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);

    const [sendNotifications, setSendNotifications] = useState(false);
    const [sendEmails, setSendEmails] = useState(false);
    const [fromDate, setFromDate] = useState(dayjs());
    const [notificationMessage, setNotificationMessage] = useState("");

    const [availableCalendars, setAvailableCalendars] = useState([]);
    const [ancestorHubs, setAncestorHubs] = useState([]);

    const [dialogMessageIsOpen, setDialogMessageIsOpen] = useState(false);
    const [dialogMessage, setDialogMessage] = useState("");

    const linkPreview = useMemo(() => getLinkPreview(url), [url]);
    const hasInvalidUrl = urlWasSubmitted && url.trim() !== "" && !linkPreview;
    const recipientCount = useMemo(
        () =>
            new Set(
                [...selectedUsers, ...managers]
                    .map((user) => user.key)
                    .filter(Boolean),
            ).size,
        [managers, selectedUsers],
    );

    useEffect(() => {
        const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
            if (!user) {
                history.replace(`/signin?url=${location.pathname}`);
            }
        });

        let managersArray = [];

        if (location.folder) {
            managersArray = location.folder.managers
                .map((managerId) =>
                    users.find((user) => user.key === managerId),
                )
                .filter(Boolean);

            let ancestors = folders.filter(
                (folder) =>
                    folder.hub &&
                    location.folder.ancestors.includes(folder.key),
            );

            ancestors = ancestors.filter(
                (ancestor) =>
                    ancestor.managers.includes(currentUserData.key) ||
                    currentUserData.role === "System Digital Manager" ||
                    ancestor.users.includes(currentUserData.key),
            );

            if (location.folder.hub) {
                ancestors.push(location.folder);
            }

            ancestors.forEach((ancestorHub) => {
                ancestorHub.groups = groups.filter(
                    (group) => group.hub === ancestorHub.key,
                );
            });

            setAncestorHubs(ancestors);

            if (
                location.folder.managers.includes(currentUserData.key) ||
                currentUserData.role === "System Digital Manager" ||
                currentUserData.permissions.uploadEditOwnVideo
            ) {
                setUserCanUpload(true);

                setAvailableCalendars(
                    calendars.filter(
                        (calendar) =>
                            calendar.user === currentUserData.key ||
                            calendar.hub === location.folder.key ||
                            location.folder.ancestors.includes(calendar.hub),
                    ),
                );
            } else {
                setUserCanUpload(false);
            }
        } else {
            history.replace("/files");
        }

        setManagers(managersArray);

        if (
            !managersArray.some(
                (manager) => manager.key === currentUserData.key,
            )
        ) {
            setSelectedUsers((currentSelectedUsers) =>
                currentSelectedUsers.length === 0
                    ? [currentUserData]
                    : currentSelectedUsers,
            );
        }

        return () => unsubscribe();
    }, [
        calendars,
        currentUserData,
        folders,
        groups,
        history,
        location.folder,
        location.pathname,
        users,
    ]);

    function isUserInSelectedGroups(user) {
        return selectedGroups.some((group) =>
            group.users.some((groupUser) => groupUser.key === user.key),
        );
    }

    async function sendLinkEmails(message, fileKey, fileDisplayName) {
        const allRecipients = [...selectedUsers, ...managers];
        const emailUsers = Array.from(
            new Map(
                allRecipients
                    .filter((user) => user && user.key)
                    .map((user) => [user.key, user]),
            ).values(),
        );

        if (emailUsers.length === 0) {
            return;
        }

        const safeDisplayName = escapeHtml(fileDisplayName);
        const messageSection = message
            ? `<tr><td style="padding: 0 30px 20px 30px; font-family: Arial, Helvetica, sans-serif; font-size: 15px; line-height: 1.5; color: #555555;">${escapeHtml(
                  message,
              ).replace(/\n/g, "<br>")}</td></tr>`
            : "";
        const link = `https://nodiant.faw.cymru/link/${fileKey}`;
        const emailHtml = `
<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>
<body style="margin: 0; padding: 0; background-color: #f4f4f4; font-family: Arial, Helvetica, sans-serif;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4;">
        <tr><td align="center" style="padding: 20px 0;">
            <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                <tr><td style="background-color: rgb(0, 149, 61); padding: 20px 30px;"><h1 style="margin: 0; font-family: Arial, Helvetica, sans-serif; font-size: 22px; color: #ffffff;">Nodiant Online</h1></td></tr>
                <tr><td style="padding: 30px 30px 10px 30px; font-family: Arial, Helvetica, sans-serif; font-size: 15px; line-height: 1.5; color: #333333;">A new link has been shared with you:</td></tr>
                <tr><td style="padding: 0 30px 20px 30px; font-family: Arial, Helvetica, sans-serif; font-size: 18px; font-weight: bold; color: #333333;">${safeDisplayName}</td></tr>
                ${messageSection}
                <tr><td style="padding: 0 30px 30px 30px;"><a href="${link}" style="display: inline-block; padding: 12px 24px; background-color: rgb(0, 149, 61); color: #ffffff; text-decoration: none; border-radius: 4px; font-family: Arial, Helvetica, sans-serif; font-size: 15px; font-weight: bold;">View Link</a></td></tr>
            </table>
        </td></tr>
    </table>
</body>
</html>`;
        // A failed notification must not fail the link creation, which is
        // what the per-recipient catch used to guarantee.
        await sendSharedItemEmails({
            embargoNotifications: true,
            firebase,
            html: emailHtml,
            label: `link: ${fileDisplayName}`,
            notificationDate: fromDate,
            recipients: emailUsers,
            subject: "Nodiant Online - New Link Available",
        }).catch((error) => {
            console.error("Could not send link notification emails", error);
        });
    }

    async function addToFirebase(key, normalisedUrl) {
        const eventsObject = {};
        events.forEach((event) => {
            eventsObject[event.key] = true;
        });

        const managersObject = {};
        managers.forEach((manager) => {
            managersObject[manager.key] = true;
        });

        const ancestorsObject = {};
        location.folder.ancestors.forEach((ancestor) => {
            ancestorsObject[ancestor] = true;
        });
        ancestorsObject[location.folder.key] = true;

        const displayNameForDatabase =
            displayName.trim() || normalisedUrl;
        const uploadedBy = currentUserData ? currentUserData.key : "";
        const usersObjectForNotifications = {};

        await firebase.database().ref("files").child(key).set({
            filename: normalisedUrl,
            displayName: displayNameForDatabase,
            folder: location.folder.id,
            type: "link",
            allowDownloads: false,
            allowComments,
            allowSharing: false,
            managers: managersObject,
            events: eventsObject,
            groups: jsonFromArray(selectedGroups),
            ancestors: ancestorsObject,
            uploadedBy,
        });

        await Promise.all([
            ...events.map((event) =>
                firebase
                    .database()
                    .ref("events")
                    .child(event.key)
                    .child("files")
                    .update({ [key]: true }),
            ),
            ...selectedUsers.map((user) => {
                usersObjectForNotifications[user.key] = true;

                if (isUserInSelectedGroups(user)) {
                    return Promise.resolve();
                }

                return firebase
                    .database()
                    .ref("users")
                    .child(user.key)
                    .child("files")
                    .update({ [key]: true });
            }),
        ]);

        if (sendNotifications) {
            await firebase.database().ref("notifications").push({
                file: key,
                title: displayNameForDatabase,
                type: "link",
                users: usersObjectForNotifications,
                managers: managersObject,
                message: notificationMessage,
                url: normalisedUrl,
                date: dayjs().toString(),
                fromDate: fromDate.toString(),
            });
        }

        if (sendEmails) {
            await sendLinkEmails(
                notificationMessage,
                key,
                displayNameForDatabase,
            );
        }
    }

    async function handleAddLink() {
        setUrlWasSubmitted(true);

        const normalisedUrl = normaliseLink(url);
        if (!location.folder || !normalisedUrl) {
            setDialogMessage(
                "Enter a valid website address. Links must use http:// or https://.",
            );
            setDialogMessageIsOpen(true);
            return;
        }

        setUrl(normalisedUrl);
        setIsSaving(true);

        try {
            await addToFirebase(makeid(19), normalisedUrl);
            history.goBack();
        } catch (error) {
            console.error("Could not add link", error);
            setDialogMessage(
                "The link could not be added. Check your connection and try again.",
            );
            setDialogMessageIsOpen(true);
            setIsSaving(false);
        }
    }

    function handleUrlBlur() {
        if (!url.trim()) {
            return;
        }

        const normalisedUrl = normaliseLink(url);
        setUrlWasSubmitted(true);

        if (normalisedUrl) {
            setUrl(normalisedUrl);
        }
    }

    function handleChange(event) {
        if (event.target.name === "comments") {
            setAllowComments(event.target.checked);
        }
    }

    function handleCloseNotificationsDialog(settings) {
        setSendNotifications(settings.sendNotifications);
        setSendEmails(settings.sendEmails);
        setNotificationMessage(settings.message);
        setFromDate(settings.fromDate);
        setNotificationsDialogOpen(false);
    }

    if (userCanUpload === false) {
        return <NoPermissionsMessage />;
    }

    if (!userCanUpload) {
        return null;
    }

    return (
        <>
            <Box
                component="main"
                sx={{
                    minHeight: "calc(100vh - 64px)",
                    pb: { xs: 11, sm: 5 },
                    backgroundColor: "background.default",
                }}
            >
                <Container
                    maxWidth="lg"
                    sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: { xs: 1, sm: 1.5 },
                            mb: { xs: 2, sm: 3 },
                        }}
                    >
                        <IconButton
                            onClick={() => history.goBack()}
                            aria-label="Back to files"
                            sx={{
                                mt: -0.35,
                                color: "text.primary",
                                backgroundColor: "background.paper",
                                border: "1px solid",
                                borderColor: "divider",
                                "&:hover": {
                                    backgroundColor: "action.hover",
                                },
                            }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                    fontSize: { xs: 27, sm: 34 },
                                    lineHeight: 1.15,
                                }}
                            >
                                Add external link
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                            >
                                {location.folder?.name
                                    ? `Adding to ${location.folder.name}`
                                    : "Add web content to Nodiant"}
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            disableElevation
                            startIcon={
                                isSaving ? (
                                    <CircularProgress
                                        size={17}
                                        color="inherit"
                                    />
                                ) : (
                                    <LinkIcon />
                                )
                            }
                            disabled={!linkPreview || isSaving}
                            onClick={handleAddLink}
                            sx={{
                                display: { xs: "none", sm: "inline-flex" },
                                minHeight: 42,
                                px: 2.25,
                                borderRadius: 2,
                            }}
                        >
                            {isSaving ? "Adding…" : "Add link"}
                        </Button>
                    </Box>

                    <Paper
                        variant="outlined"
                        sx={{
                            overflow: "hidden",
                            borderRadius: 3,
                            borderColor: "divider",
                            boxShadow: (theme) =>
                                theme.palette.mode === "dark"
                                    ? "0 5px 22px rgba(0, 0, 0, 0.3)"
                                    : "0 5px 22px rgba(20, 35, 25, 0.055)",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                px: { xs: 1.75, sm: 2.5 },
                                py: 1.75,
                                borderBottom: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            <Box
                                sx={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: 2,
                                    display: "grid",
                                    placeItems: "center",
                                    flexShrink: 0,
                                    color: "primary.main",
                                    backgroundColor: "action.hover",
                                }}
                            >
                                <LinkIcon />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 800 }}
                                >
                                    Link details
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Paste once, preview instantly, then choose
                                    who can see it.
                                </Typography>
                            </Box>
                            {linkPreview && (
                                <Chip
                                    label={linkPreview.label}
                                    size="small"
                                    sx={{
                                        display: { xs: "none", sm: "flex" },
                                        backgroundColor: primaryTint,
                                        color: "primary.main",
                                    }}
                                />
                            )}
                        </Box>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    md: "minmax(0, 0.88fr) minmax(420px, 1.12fr)",
                                },
                                alignItems: "stretch",
                            }}
                        >
                            <Box
                                sx={{
                                    p: { xs: 1.75, sm: 2.5 },
                                    borderRight: { md: "1px solid" },
                                    borderColor: "divider",
                                    backgroundColor: "background.subtle",
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 1, px: 0.5, fontWeight: 800 }}
                                >
                                    Web address
                                </Typography>
                                <TextField
                                    autoFocus
                                    value={url}
                                    onChange={(event) => {
                                        setUrl(event.target.value);
                                        setUrlWasSubmitted(false);
                                    }}
                                    onBlur={handleUrlBlur}
                                    label="Paste a link"
                                    placeholder="https://www.youtube.com/watch?v=…"
                                    helperText={
                                        hasInvalidUrl
                                            ? "Enter a valid web address."
                                            : "You can paste the address with or without https://."
                                    }
                                    error={hasInvalidUrl}
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <ContentPasteOutlinedIcon
                                                    color="action"
                                                    fontSize="small"
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 2,
                                            backgroundColor:
                                                "background.paper",
                                        },
                                    }}
                                />

                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        mt: 2.25,
                                        mb: 1,
                                        px: 0.5,
                                        fontWeight: 800,
                                    }}
                                >
                                    Name in Nodiant
                                </Typography>
                                <TextField
                                    value={displayName}
                                    onChange={(event) =>
                                        setDisplayName(event.target.value)
                                    }
                                    label="Display name"
                                    placeholder={
                                        linkPreview
                                            ? linkPreview.label
                                            : "e.g. Training analysis"
                                    }
                                    helperText="Optional — leave blank to use the web address."
                                    fullWidth
                                    inputProps={{ maxLength: 200 }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 2,
                                            backgroundColor:
                                                "background.paper",
                                        },
                                    }}
                                />
                            </Box>

                            <Box
                                sx={{
                                    minWidth: 0,
                                    borderTop: { xs: "1px solid", md: 0 },
                                    borderColor: "divider",
                                }}
                            >
                                <LinkPreview
                                    link={linkPreview}
                                    title={displayName}
                                />
                            </Box>
                        </Box>
                    </Paper>

                    <Paper
                        variant="outlined"
                        sx={{
                            mt: { xs: 2, sm: 2.5 },
                            p: { xs: 1.75, sm: 2.5 },
                            borderRadius: 3,
                            borderColor: "divider",
                            boxShadow: (theme) =>
                                theme.palette.mode === "dark"
                                    ? "0 4px 18px rgba(0, 0, 0, 0.28)"
                                    : "0 4px 18px rgba(20, 35, 25, 0.045)",
                        }}
                    >
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    md: "minmax(0, 1.25fr) minmax(300px, 0.75fr)",
                                },
                                gap: { xs: 2.5, md: 3 },
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 1, px: 0.5, fontWeight: 800 }}
                                >
                                    Link settings
                                </Typography>
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: {
                                            xs: "1fr",
                                            sm: "repeat(3, minmax(0, 1fr))",
                                        },
                                        gap: 1,
                                    }}
                                >
                                    <ButtonBase
                                        onClick={() =>
                                            setEventDialogOpen(true)
                                        }
                                        sx={settingButtonSx}
                                    >
                                        <Badge
                                            badgeContent={events.length}
                                            color="primary"
                                            max={99}
                                        >
                                            <EventNoteIcon color="primary" />
                                        </Badge>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: 800 }}
                                            >
                                                Events
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {events.length === 0
                                                    ? "No linked events"
                                                    : `${events.length} linked`}
                                            </Typography>
                                        </Box>
                                        <ChevronRightIcon
                                            sx={{ color: "text.disabled" }}
                                        />
                                    </ButtonBase>

                                    <ButtonBase
                                        onClick={() =>
                                            setNotificationsDialogOpen(true)
                                        }
                                        sx={settingButtonSx}
                                    >
                                        <NotificationsIcon color="primary" />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: 800 }}
                                            >
                                                Notifications
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {sendNotifications ||
                                                sendEmails
                                                    ? "Configured"
                                                    : "Not configured"}
                                            </Typography>
                                        </Box>
                                        <ChevronRightIcon
                                            sx={{ color: "text.disabled" }}
                                        />
                                    </ButtonBase>

                                    <ButtonBase
                                        onClick={() =>
                                            setPermissionsDialogOpen(true)
                                        }
                                        sx={settingButtonSx}
                                    >
                                        <GroupIcon color="primary" />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: 800 }}
                                            >
                                                Access
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {recipientCount}{" "}
                                                {recipientCount === 1
                                                    ? "person"
                                                    : "people"}{" "}
                                                selected
                                            </Typography>
                                        </Box>
                                        <ChevronRightIcon
                                            sx={{ color: "text.disabled" }}
                                        />
                                    </ButtonBase>
                                </Box>
                            </Box>

                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 1, px: 0.5, fontWeight: 800 }}
                                >
                                    Availability
                                </Typography>
                                <Box
                                    sx={{
                                        borderRadius: 2.5,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        overflow: "hidden",
                                        backgroundColor: "background.paper",
                                    }}
                                >
                                    <AvailabilityRow
                                        icon={<ChatBubbleOutlineIcon />}
                                        title="Comments"
                                        description="Allow users to discuss this link."
                                        checked={allowComments}
                                        onChange={handleChange}
                                        name="comments"
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </Container>

                <Box
                    sx={{
                        display: { xs: "flex", sm: "none" },
                        position: "fixed",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 2,
                        p: 1.5,
                        borderTop: "1px solid",
                        borderColor: "divider",
                        backgroundColor: (theme) =>
                            alpha(theme.palette.background.paper, 0.96),
                        backdropFilter: "blur(12px)",
                    }}
                >
                    <Button
                        variant="contained"
                        fullWidth
                        disableElevation
                        disabled={!linkPreview || isSaving}
                        onClick={handleAddLink}
                        startIcon={
                            isSaving ? (
                                <CircularProgress size={17} color="inherit" />
                            ) : (
                                <LinkIcon />
                            )
                        }
                        sx={{ minHeight: 46, borderRadius: 2 }}
                    >
                        {isSaving ? "Adding link…" : "Add link"}
                    </Button>
                </Box>
            </Box>

            <LinkFileToEventDialog
                open={eventDialogOpen}
                onClose={(selectedEvents) => {
                    setEvents(selectedEvents);
                    setEventDialogOpen(false);
                }}
                selectionChanged={setEvents}
                selectedEvents={events}
                calendars={availableCalendars}
            />
            <FilePermissionsDialog
                open={permissionsDialogOpen}
                onClose={(nextUsers, nextGroups) => {
                    setSelectedGroups(nextGroups);
                    setSelectedUsers(nextUsers);
                    setPermissionsDialogOpen(false);
                }}
                selectedUsers={selectedUsers}
                selectedGroups={selectedGroups}
                managers={managers}
                ancestorHubs={ancestorHubs}
            />
            <FileNotificationsDialog
                open={notificationsDialogOpen}
                onClose={handleCloseNotificationsDialog}
                message={notificationMessage}
                send={sendNotifications}
                email={sendEmails}
                fromDate={fromDate}
                recipientCount={recipientCount}
            />
            <DialogWithMessage
                open={dialogMessageIsOpen}
                onClose={() => setDialogMessageIsOpen(false)}
                title="Could not add link"
                message={dialogMessage}
            />
        </>
    );
}
